"use client";

import { Typography } from "@/components/custom/typography";
import { useTranslations } from "next-intl";
import Image from "next/image";
import React from "react";

type StepIndicatorProps = {
  step1Label?: string;
  step2Label?: string;
  currentStep?: 1 | 2 | 3;
};

export default function StepIndicator({
  step1Label,
  step2Label,
  currentStep = 1,
}: StepIndicatorProps) {
  const t = useTranslations("button.stepIndicator");

  const effectiveStep1 = step1Label ?? t("step1");
  const effectiveStep2 = step2Label ?? t("step2");

  return (
    <div className="flex items-center mb-3 text-xs font-medium">
      {/* Step 1 */}
      <div className="flex items-center gap-1">
        {currentStep === 1 ? (
          <div className="flex items-center gap-2.5 bg-primary-400 text-white px-1.5 py-1 rounded">
            <div className="flex items-center justify-center w-4 h-4 text-sm">
              1
            </div>
            <Typography as="span" variant="body3" className="text-white">
              {effectiveStep1}
            </Typography>
          </div>
        ) : (
          <div className="flex items-center justify-center w-6 h-6 rounded bg-primary-200 text-white font-bold text-sm">
            1
          </div>
        )}
      </div>

      {/* Dotted Line */}
      <div className="w-2.5 border-t-4 border-dotted border-gray-soft mx-1"></div>

      {/* Step 2 */}
      <div className="flex items-center gap-1">
        {currentStep === 2 ? (
          <div className="flex items-center gap-2.5 bg-primary-400 text-white pr-1.5 py-0.5 rounded">
            <div className="flex items-center justify-center w-5 h-5 text-sm">
              2
            </div>
            <Typography as="span" variant="body3" className="text-white">
              {effectiveStep2}
            </Typography>
          </div>
        ) : (
          <div className="flex items-center justify-center w-5 h-5 rounded bg-gray-400 text-white font-bold text-sm">
            2
          </div>
        )}
      </div>

      {/* Dotted Line */}
      <div className="w-2.5 border-t-4 border-dotted border-gray-400 mx-1"></div>

      {/* Step 3 (Flag icon) */}
      <div className="flex items-center gap-1">
        <div
          className={`flex items-center justify-center w-5 h-5 rounded ${
            currentStep === 3 ? "bg-black text-white" : "bg-gray-400 text-white"
          }`}
        >
          <Image
            src="/images/flag-icon.svg"
            alt="logo"
            width={20}
            height={20}
          />
        </div>
      </div>
    </div>
  );
}
